// A Schlage site's DB download cadence: how often its Wi-Fi (mode 210) locks call in for a new database (sent to
// each lock as dbDwnLdTm). Mirrors ConnectApi's DbDownloadCadence
// (src/Livly.Connect.Models/Engage/DbDownloadCadence.cs) -- not yet in the NSwag-generated client.

// Radix SelectItem can't use "" as a value, so "no cadence" needs a sentinel.
export const CADENCE_DEFAULT = "default";

// Sent as dbDownloadIntervalMinutes to clear the cadence (null on an update means "leave unchanged").
export const CLEAR_INTERVAL_MINUTES = 0;

// What locks do without a cadence. Observed in the field, not documented by Schlage.
export const DEFAULT_CALL_IN_HOURS = 24;

const MINUTES_PER_DAY = 1440;

export const DB_DOWNLOAD_INTERVAL_OPTIONS: { value: number; label: string }[] = [
  { value: 1440, label: "Daily" },
  { value: 720, label: "Every 12 hours" },
  { value: 480, label: "Every 8 hours" },
  { value: 360, label: "Every 6 hours" },
  { value: 240, label: "Every 4 hours" },
  { value: 180, label: "Every 3 hours" },
  { value: 120, label: "Every 2 hours" },
  { value: 60, label: "Every hour" },
];

export const DEFAULT_CADENCE_LABEL = `Default (every ${DEFAULT_CALL_IN_HOURS} h, time of day not controlled)`;

/** 0-1439 -> "HH:mm". */
export function minuteOfDayToTime(minuteOfDay: number | null | undefined): string {
  const m = Math.min(Math.max(minuteOfDay ?? 0, 0), MINUTES_PER_DAY - 1);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** "HH:mm" -> 0-1439, or undefined when it isn't a time. */
export function timeToMinuteOfDay(time: string): number | undefined {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) return undefined;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return undefined;
  return hours * 60 + minutes;
}

/** e.g. "Daily at 02:00 (America/Chicago)" or "Every 6 hours from 06:00 (America/Chicago)". */
export function cadenceLabel(
  intervalMinutes: number | null | undefined,
  anchorMinuteOfDay: number | null | undefined,
  timeZone?: string | null,
): string {
  if (!intervalMinutes) return DEFAULT_CADENCE_LABEL;
  const time = minuteOfDayToTime(anchorMinuteOfDay);
  const zone = timeZone ? ` (${timeZone})` : "";
  if (intervalMinutes === MINUTES_PER_DAY) return `Daily at ${time}${zone}`;
  const option = DB_DOWNLOAD_INTERVAL_OPTIONS.find((o) => o.value === intervalMinutes);
  return `${option?.label ?? `Every ${intervalMinutes} minutes`} from ${time}${zone}`;
}

/**
 * True when the cadence puts call-ins in the daytime (06:00-22:00), when residents notice the few seconds a lock
 * ignores cards while it calls in. Any interval under a day does; a daily one does when anchored in that window.
 */
export function hasDaytimeCallIns(
  intervalMinutes: number | null | undefined,
  anchorMinuteOfDay: number | null | undefined,
): boolean {
  if (!intervalMinutes) return false;
  if (intervalMinutes < MINUTES_PER_DAY) return true;
  const anchor = anchorMinuteOfDay ?? 0;
  return anchor >= 6 * 60 && anchor < 22 * 60;
}
